import React, { useState, useEffect } from 'react';
import { fetchReviews } from 'shared/api/api';
import { useTranslation } from 'react-i18next';

const TestimonialSlider = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const { i18n } = useTranslation();

    useEffect(() => {
        const loadTestimonials = async () => {
            try {
                const response = await fetchReviews();
                // Filter reviews by current language and active status
                const filtered = response.data.filter(review =>
                    review.is_active && review.language === i18n.language
                );
                setTestimonials(filtered);
                setLoading(false);
            } catch (error) {
                console.error('Error loading testimonials:', error);
                setLoading(false);
            }
        };

        loadTestimonials();
    }, [i18n.language]);

    useEffect(() => {
        if (testimonials.length === 0) return;

        const timer = setInterval(() => {
            setCurrentIndex((prevIndex) =>
                (prevIndex + 1 >= testimonials.length) ? 0 : prevIndex + 1
            );
        }, 5000);

        return () => clearInterval(timer);
    }, [testimonials.length]);

    const StarIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
            <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z" clipRule="evenodd" />
        </svg>
    );

    if (loading) {
        return <div className="animate-pulse bg-gray-100 h-96 rounded-lg"></div>;
    }

    if (testimonials.length === 0) {
        return null;
    }

    return (
        <section className="relative isolate overflow-hidden bg-white px-6 py-24 sm:py-32 lg:px-8 rounded-lg">
            <div className="mx-auto max-w-2xl lg:max-w-4xl">
                <figure className="mt-10">
                    <figcaption className="mb-6">
                        <div className="flex flex-col items-center mb-4">
                            <div className="font-semibold text-gray-900 text-lg md:text-base text-center">{testimonials[currentIndex].name}</div>
                            <div className="flex justify-center mt-2 mb-2">
                                {[...Array(testimonials[currentIndex].stars)].map((_, i) => (
                                    <span key={i} className="text-yellow-400">
                                        <StarIcon/>
                                    </span>
                                ))}
                            </div>
                            <div className="text-gray-600 text-sm md:text-base text-center font-medium">{testimonials[currentIndex].title}</div>
                        </div>
                        <img
                            className="mx-auto h-16 w-16 rounded-full object-cover mb-4"
                            src={testimonials[currentIndex].image_url}
                            alt={testimonials[currentIndex].name}
                        />
                    </figcaption>
                    <blockquote className="text-center text-xl/8 font-semibold text-gray-900 sm:text-2xl/9">
                        <p>"{testimonials[currentIndex].body}"</p>
                    </blockquote>
                </figure>

                <div className="mt-8 flex justify-center space-x-2">
                    {testimonials.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentIndex(index)}
                            className={`h-2 w-2 rounded-full transition-colors ${
                                currentIndex === index ? 'bg-sky-600' : 'bg-sky-200'
                            }`}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default TestimonialSlider;